import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Honeybadger from '@honeybadger-io/js';

import { useAuth } from '../../../../../shared/hooks';
import { deserializer } from '../../../../../shared/utils';
import { fetchTags as apiFetchTags } from '../../../../shared/services';
import {
  clearFilterByType,
  toggleFilterValue,
  withCurrentUserFirst,
} from '../../../../shared/utils';
import { fetchOwners as apiFetchOwners } from '../../services/ownerService';
import DecedentFilterContext from './DecedentFilterContext';

const INITIAL_ACTIVE_FILTERS = {
  owners: [],
  tags: [],
};

const INITIAL_FILTER_OPTIONS = {
  owners: [],
  tags: [],
};

const DecedentFilterProvider = ({ children }) => {
  const [activeFilters, setActiveFilters] = useState(INITIAL_ACTIVE_FILTERS);
  const [filterOptions, setFilterOptions] = useState(INITIAL_FILTER_OPTIONS);

  const {
    user: { id: currentUserId },
  } = useAuth();

  const clearFilters = useCallback((type) => {
    setActiveFilters((prev) => clearFilterByType(prev, type));
  }, []);

  const toggleFilter = useCallback(({ type, value }) => {
    setActiveFilters((prev) => toggleFilterValue(prev, { type, value }));
  }, []);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [ownersResponse, tagsResponse] = await Promise.all([
          apiFetchOwners(),
          apiFetchTags('Decedent'),
        ]);

        const owners = await deserializer.deserialize(ownersResponse);
        const tags = await deserializer.deserialize(tagsResponse);

        setFilterOptions({
          owners: withCurrentUserFirst({ currentUserId, users: owners }),
          tags,
        });
      } catch (error) {
        toast.error(error.message);
        Honeybadger.notify(error);
      }
    };

    fetchFilterOptions();
  }, [currentUserId]);

  const contextValue = useMemo(
    () => ({
      activeFilters,
      clearFilters,
      filterOptions,
      toggleFilter,
    }),
    [activeFilters, clearFilters, filterOptions, toggleFilter]
  );

  return (
    <DecedentFilterContext.Provider value={contextValue}>
      {children}
    </DecedentFilterContext.Provider>
  );
};

DecedentFilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DecedentFilterProvider;
